/**
 * EvidenceTable — Iter-38 Stage 2 (with mobile-stack refinement).
 *
 * Desktop (sm and up): renders as a compact table with columns adapted to
 *   the evidence_type.
 * Mobile (< sm): renders each evidence row as a stacked card so URLs, HTTP
 *   status, and actions stay readable without horizontal scrolling.
 *
 * Never invents data — if `evidence[]` is empty the component returns null
 * so the parent can decide whether to show a calm fallback.
 */
import { useState } from "react";
import { ExternalLink, Copy, Check } from "lucide-react";

const URL_CLS =
  "text-charcoal-800 hover:text-brand hover:underline font-mono text-[11px] break-all";

function ExtLink({ href, children, ...rest }) {
  if (!href) return <span>{children}</span>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={URL_CLS} {...rest}>
      {children}
    </a>
  );
}

function CopyBtn({ value, testId }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
        } catch {
          /* ignore */
        }
        setDone(true);
        setTimeout(() => setDone(false), 1400);
      }}
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-charcoal-500 hover:text-charcoal-800 transition-colors"
      data-testid={testId}
      aria-label={`Copy ${value}`}
    >
      {done ? (
        <>
          <Check className="w-3 h-3" /> Copied
        </>
      ) : (
        <>
          <Copy className="w-3 h-3" /> Copy
        </>
      )}
    </button>
  );
}

function OpenBtn({ href, label = "Open", testId }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-charcoal-500 hover:text-charcoal-800 transition-colors"
      data-testid={testId}
    >
      <ExternalLink className="w-3 h-3" /> {label}
    </a>
  );
}

function HttpChip({ code }) {
  if (!code) return <span className="text-charcoal-400">—</span>;
  return (
    <span className="inline-block font-mono text-[11px] px-1.5 py-0.5 rounded bg-charcoal-100 text-charcoal-700">
      {code}
    </span>
  );
}

function scopeOf(url) {
  if (!url) return "—";
  try {
    const u = new URL(url);
    if (typeof window !== "undefined" && window.location?.hostname) {
      const site = window.location.hostname.replace(/^www\./, "");
      const target = u.hostname.replace(/^www\./, "");
      if (!target) return "—";
      if (target === site || target.endsWith(`.${site}`)) return "Internal";
    }
    return "External";
  } catch {
    return "—";
  }
}

function columnsFor(type) {
  switch (type) {
    case "broken_link":
      return ["Broken URL", "Reason", "HTTP", "Scope", "Actions"];
    case "failing_page":
      return ["Page", "Issue", "HTTP Code", "Actions"];
    case "missing_alt":
      return ["Image", "Page", "Recommendation", "Actions"];
    default:
      return ["Location", "Detail", "Actions"];
  }
}

// -------------------- Desktop table rows --------------------

const CELL_CLS = "align-top py-1.5 pr-3 text-[12px] text-charcoal-700 break-all";
const CELL_NOWRAP = "align-top py-1.5 pr-3 text-[12px] text-charcoal-700 whitespace-nowrap";

function DesktopRow({ type, ev, prefix, idx }) {
  const testId = `${prefix}-row-${idx}`;
  if (type === "broken_link") {
    return (
      <tr data-testid={testId}>
        <td className={CELL_CLS}><ExtLink href={ev.url}>{ev.url}</ExtLink></td>
        <td className={CELL_CLS}>{ev.reason || "—"}</td>
        <td className={CELL_NOWRAP}><HttpChip code={ev.http_status} /></td>
        <td className={CELL_NOWRAP}>
          <span className="text-[11px] font-mono text-charcoal-500 uppercase tracking-wider">{scopeOf(ev.url)}</span>
        </td>
        <td className={CELL_NOWRAP}>
          <div className="flex items-center gap-3 whitespace-nowrap">
            <CopyBtn value={ev.url} testId={`${testId}-copy`} />
            <OpenBtn href={ev.url} testId={`${testId}-open`} />
          </div>
        </td>
      </tr>
    );
  }
  if (type === "failing_page") {
    return (
      <tr data-testid={testId}>
        <td className={CELL_CLS}><ExtLink href={ev.url}>{ev.url}</ExtLink></td>
        <td className={CELL_CLS}>{ev.reason || "—"}</td>
        <td className={CELL_NOWRAP}><HttpChip code={ev.http_status} /></td>
        <td className={CELL_NOWRAP}>
          <div className="flex items-center gap-3 whitespace-nowrap">
            <CopyBtn value={ev.url} testId={`${testId}-copy`} />
            <OpenBtn href={ev.url} label="Open Page" testId={`${testId}-open`} />
          </div>
        </td>
      </tr>
    );
  }
  if (type === "missing_alt") {
    return (
      <tr data-testid={testId}>
        <td className={CELL_CLS}><ExtLink href={ev.url}>{ev.url}</ExtLink></td>
        <td className={CELL_CLS}>
          {ev.page_url ? <ExtLink href={ev.page_url}>{ev.page_url}</ExtLink> : <span className="text-charcoal-400">—</span>}
        </td>
        <td className={CELL_CLS}>Add descriptive alt text</td>
        <td className={CELL_NOWRAP}><CopyBtn value={ev.url} testId={`${testId}-copy`} /></td>
      </tr>
    );
  }
  return (
    <tr data-testid={testId}>
      <td className={CELL_CLS}><ExtLink href={ev.url}>{ev.url || ev.element || "—"}</ExtLink></td>
      <td className={CELL_CLS}>{ev.reason || "—"}</td>
      <td className={CELL_NOWRAP}><CopyBtn value={ev.url} testId={`${testId}-copy`} /></td>
    </tr>
  );
}

// -------------------- Mobile stacked cards --------------------

function MobileRow({ type, ev, prefix, idx }) {
  const testId = `${prefix}-row-${idx}`;
  // Every mobile row has the same rhythm: URL (primary) → meta line (Reason /
  // HTTP / Scope pills) → actions row. Long URLs wrap on word/character
  // breaks INSIDE the card — never off-screen.
  const meta = [];
  if (ev.reason) meta.push({ label: type === "missing_alt" ? "Reason" : "Reason", value: ev.reason });
  if (ev.http_status) meta.push({ label: "HTTP", value: ev.http_status });
  const showScope = type === "broken_link";

  return (
    <div
      className="border-t border-black/5 first:border-t-0 py-2.5 px-3"
      data-testid={testId}
    >
      {/* Primary URL/image */}
      <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-charcoal-500 mb-0.5">
        {type === "missing_alt" ? "Image" : type === "failing_page" ? "Page" : "URL"}
      </div>
      <ExtLink href={ev.url}>{ev.url}</ExtLink>

      {/* Secondary page context (missing alt) */}
      {type === "missing_alt" && ev.page_url && (
        <div className="mt-1.5">
          <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-charcoal-500 mb-0.5">Page</div>
          <ExtLink href={ev.page_url}>{ev.page_url}</ExtLink>
        </div>
      )}

      {/* Meta pills */}
      {(meta.length > 0 || showScope) && (
        <div className="mt-2 flex items-center flex-wrap gap-2">
          {ev.http_status && (
            <span className="inline-block font-mono text-[11px] px-1.5 py-0.5 rounded bg-charcoal-100 text-charcoal-700">
              HTTP {ev.http_status}
            </span>
          )}
          {ev.reason && !/^HTTP\s+\d+/i.test(ev.reason) && (
            <span className="text-[11px] text-charcoal-600">{ev.reason}</span>
          )}
          {showScope && (
            <span className="text-[10px] font-mono uppercase tracking-wider text-charcoal-500">
              {scopeOf(ev.url)}
            </span>
          )}
        </div>
      )}

      {/* Missing-alt recommendation */}
      {type === "missing_alt" && (
        <p className="mt-2 text-[12px] text-charcoal-700">Add descriptive alt text</p>
      )}

      {/* Actions row — always visible, no scroll needed */}
      <div className="mt-2 flex items-center gap-4">
        <CopyBtn value={ev.url} testId={`${testId}-copy`} />
        {type !== "missing_alt" && (
          <OpenBtn
            href={ev.url}
            label={type === "failing_page" ? "Open Page" : "Open"}
            testId={`${testId}-open`}
          />
        )}
      </div>
    </div>
  );
}

// -------------------- Main --------------------

const COL_HEAD_CLS =
  "text-left text-[10px] font-mono uppercase tracking-[0.14em] text-charcoal-500 pb-1.5 pr-3 whitespace-nowrap";

export default function EvidenceTable({ check }) {
  const evidence = Array.isArray(check?.evidence) ? check.evidence : null;
  const meta = check?.evidence_meta || null;
  const [showAll, setShowAll] = useState(false);
  if (!evidence || !evidence.length) return null;

  const type = evidence[0]?.evidence_type || "generic";
  const cols = columnsFor(type);
  const initial = meta?.initial_visible || Math.min(10, evidence.length);
  const rows = showAll ? evidence : evidence.slice(0, initial);
  const prefix = `evidence-${check.key || "generic"}`;

  return (
    <div className="mt-3" data-testid={prefix}>
      <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-charcoal-500 mb-1.5">
        Where it happened
      </p>

      {/* Desktop table (sm and up) */}
      <div className="hidden sm:block rounded-lg bg-charcoal-50/70 border border-black/5 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c} className={`${COL_HEAD_CLS} pt-2 first:pl-3 last:pr-3`}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="[&>tr]:border-t [&>tr]:border-black/5 [&>tr>td:first-child]:pl-3 [&>tr>td:last-child]:pr-3">
            {rows.map((ev, i) => (
              <DesktopRow key={i} type={type} ev={ev} prefix={prefix} idx={i} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked cards (< sm) */}
      <div className="sm:hidden rounded-lg bg-charcoal-50/70 border border-black/5">
        {rows.map((ev, i) => (
          <MobileRow key={i} type={type} ev={ev} prefix={prefix} idx={i} />
        ))}
      </div>

      {(meta?.total_hits > initial || evidence.length > initial) && (
        <div className="mt-2 flex items-center justify-between text-[11px] font-mono">
          <span className="text-charcoal-400">
            Showing {rows.length} of {meta?.total_hits ?? evidence.length}
            {meta?.capped ? ` (first ${evidence.length} captured)` : ""}
          </span>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-brand hover:underline"
            data-testid={`${prefix}-toggle`}
          >
            {showAll ? "Show fewer" : `Show all ${evidence.length}`}
          </button>
        </div>
      )}
    </div>
  );
}
